/**
 * 动态表单弹窗 — 字段渲染
 */

import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Switch,
  Tooltip,
  theme,
} from 'antd';
import type React from 'react';
import { CustomFieldAdapter } from '../CustomFieldAdapter';
import { toAntdRules } from './form-rules';
import type { DynamicFormField } from './types';

/**
 * 渲染完整的表单项（label + input）
 */
export function FormItemRenderer({
  field,
  requiredMessage,
}: {
  field: DynamicFormField;
  /** 自定义必填消息模板，使用 `{label}` 占位符 */
  requiredMessage?: string;
}): React.ReactNode {
  const { token } = theme.useToken();

  const label = field.tooltip ? (
    <span>
      {field.label}{' '}
      <Tooltip title={field.tooltip}>
        <QuestionCircleOutlined style={{ color: token.colorTextQuaternary }} />
      </Tooltip>
    </span>
  ) : (
    field.label
  );

  // switch 特殊处理 valuePropName
  const extraProps: Record<string, unknown> = {};
  if (field.type === 'switch') {
    extraProps.valuePropName = 'checked';
  }

  return (
    <Form.Item
      name={field.name}
      label={label}
      rules={toAntdRules(field, requiredMessage)}
      {...extraProps}
    >
      <FieldInput field={field} />
    </Form.Item>
  );
}

/**
 * 渲染字段输入控件
 */
function FieldInput({ field }: { field: DynamicFormField }): React.ReactNode {
  switch (field.type) {
    case 'input':
      return (
        <Input
          placeholder={field.placeholder ?? `请输入${field.label}`}
          disabled={field.disabled}
        />
      );

    case 'password':
      return (
        <Input.Password
          placeholder={field.placeholder ?? `请输入${field.label}`}
          disabled={field.disabled}
        />
      );

    case 'textarea':
      return (
        <Input.TextArea
          placeholder={field.placeholder ?? `请输入${field.label}`}
          disabled={field.disabled}
          rows={field.rows ?? 3}
        />
      );

    case 'number':
      return (
        <InputNumber
          style={{ width: '100%' }}
          placeholder={field.placeholder ?? `请输入${field.label}`}
          disabled={field.disabled}
        />
      );

    case 'select':
      return (
        <Select
          placeholder={field.placeholder ?? `请选择${field.label}`}
          disabled={field.disabled}
          options={field.options?.map((opt) => ({
            label: opt.label,
            value: opt.value,
            disabled: opt.disabled,
          }))}
          allowClear
        />
      );

    case 'radio':
      return (
        <Radio.Group disabled={field.disabled}>
          {field.options?.map((opt) => (
            <Radio
              key={String(opt.value)}
              value={opt.value}
              disabled={opt.disabled}
            >
              {opt.label}
            </Radio>
          ))}
        </Radio.Group>
      );

    case 'checkbox':
      return (
        <Checkbox.Group disabled={field.disabled}>
          {field.options?.map((opt) => (
            <Checkbox
              key={String(opt.value)}
              value={opt.value}
              disabled={opt.disabled}
            >
              {opt.label}
            </Checkbox>
          ))}
        </Checkbox.Group>
      );

    case 'switch':
      return <Switch disabled={field.disabled} />;

    case 'date':
      return <DatePicker style={{ width: '100%' }} disabled={field.disabled} />;

    case 'dateRange':
      return (
        <DatePicker.RangePicker
          style={{ width: '100%' }}
          disabled={field.disabled}
        />
      );

    case 'custom':
      if (field.render) {
        return <CustomFieldAdapter render={field.render} />;
      }
      return null;

    default:
      return null;
  }
}
